import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Card, Col, Form, Layout, PageHeader, Row, Space } from "antd";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import { camelCase } from "lodash";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";

import { PriceForm } from "./Form";

const priceListsQuery = gql`
  query($pagination: ConnectionPagination, $sorting: ValuePriceListSorting, $searchQuery: String) {
    valuePriceLists(pagination: $pagination, sorting: $sorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const createValuePriceMutation = gql`
  mutation($input: CreateValuePriceInput!) {
    createValuePrice(input: $input) {
      price {
        id
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const New: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const itemId = useParams().id;
  const navigate = useNavigate();

  const { error, loading: queryLoading, data } = useQuery(priceListsQuery, {
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(createValuePriceMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: camelCase(graphQLError.name), errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("..");
    },
  });

  const onFinish = (values: any) => {
    const input = {
      ...values,
      itemId,
    };
    execMutation({ variables: { input } });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="..">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  return (
    <Fragment>
      <PageHeader
        ghost={true}
        title="New Price"
        extra={
          <Row gutter={[24, 0]}>
            <Col>
              <NavLink to="..">
                <Button>Cancel</Button>
              </NavLink>
            </Col>
            <Col>
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Col>
          </Row>
        }
      />
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            {otherErrors.length > 0 && (
              <Space direction="vertical" style={{ marginBottom: "10px" }}>
                {otherErrors.map((otherError, index) => (
                  <Alert key={index} type="error" message="Error" description={otherError} />
                ))}
              </Space>
            )}
            <PriceForm form={form} data={data} onFinish={onFinish} />
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
